/*! *********************************************************************
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React, { useState } from 'react';
import {
  SimpleShowLayout,
  ArrayField,
  FileField,
  FunctionField,
  TextField,
  Datagrid,
  Confirm,
  useRefresh,
} from 'react-admin';
import _get from 'lodash/get';
import _head from 'lodash/head';
import _size from 'lodash/size';
import _toUpper from 'lodash/toUpper';
import _toLower from 'lodash/toLower';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Grid from '@material-ui/core/Grid';
import DeleteIcon from '@material-ui/icons/Delete';
import filesize from 'filesize';

import { DELETE_ATTACHMENT } from '../graphql/attachments';
import { mutateGraphQL } from '../helpers/dataHelpers';
import { MAIN_DOWNLOAD_FORMAT, DOWNLOAD_FORMATS_LABEL } from '../models/asset';

export default (props) => {
  const { source } = props;
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const assetType = _get(props, `record.type`);
  const revisionsSize = _size(_get(props, `record[${source}]`));
  const isDefaultFileFormat = MAIN_DOWNLOAD_FORMAT[assetType] === source;

  const handleClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);
  const handleConfirm = async () => {
    const record = _get(props, 'record');
    const assetId = _get(record, 'id');
    const attachmentId = _get(_head(record[source]), 'id');

    await mutateGraphQL(DELETE_ATTACHMENT, {
      assetId,
      attachmentId,
    });
    refresh();
    setOpen(false);
  };

  return (
    <SimpleShowLayout>
      <ArrayField
        source={source}
        label={
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            {DOWNLOAD_FORMATS_LABEL[_toLower(source)]}
            {revisionsSize > 0 && !isDefaultFileFormat && (
              <Tooltip title={`Delete ${_toUpper(source)} attachments`}>
                <IconButton aria-label="Delete" onClick={handleClick}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        }
      >
        <Datagrid>
          <FileField source="url" title="title" target="_blank" />
          <FunctionField
            source="size"
            render={(record) => filesize(record.size)}
          />
          <TextField source="revision" />
        </Datagrid>
      </ArrayField>
      <Confirm
        isOpen={open}
        title={`Delete ${_toUpper(source)} attachments`}
        content="Are you sure you want to delete these attachments?"
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </SimpleShowLayout>
  );
};
